import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getLandingPageDistribution } from "../../../../util/analyticsApi";

const COLORS = [
  "#4caf50", "#2196f3", "#ff9800", "#9c27b0", "#f44336",
  "#00bcd4", "#795548", "#607d8b", "#e91e63", "#cddc39",
];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Breadcrumb = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  margin-bottom: 16px;
  font-size: 14px;
  color: #666;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  color: #4caf50;
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #999;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
`;

const HintText = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 8px;
`;

const CustomTooltipWrapper = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
`;

const renderCustomLabel = ({ name, percent }) => {
  if (percent < 0.03) return null;
  return `${name} (${(percent * 100).toFixed(1)}%)`;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  return (
    <CustomTooltipWrapper>
      <strong>{name}</strong>
      <div>{value.toLocaleString()} sessions</div>
    </CustomTooltipWrapper>
  );
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );
  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);
  useEffect(function setupResizeListener() {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);
  return isMobile;
};

const LandingPagePieChart = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillPath, setDrillPath] = useState(null);
  const isMobile = useIsMobile();

  useEffect(function fetchLandingPageDistribution() {
    const load = async () => {
      setLoading(true);
      try {
        const result = await getLandingPageDistribution(filters);
        setData(result);
      } catch (err) {
        console.error("Error loading landing page distribution:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters]);

  // Reset drill-down when filters change
  useEffect(function resetDrillDownWhenFiltersChange() {
    setDrillPath(null);
  }, [filters]);

  if (loading) return <LoadingMessage>Loading landing page data...</LoadingMessage>;
  if (!data || data.length === 0) {
    return (
      <NoDataMessage>
        No session data available yet. Landing page tracking requires the sessionId field,
        which is only present on new analytics events.
      </NoDataMessage>
    );
  }

  // Determine chart data based on drill state
  let chartData;
  let breadcrumbs = [{ label: "All", path: null }];

  if (drillPath) {
    const parent = data.find((d) => d.path === drillPath);
    if (parent && parent.children.length > 0) {
      chartData = parent.children.map((c) => ({ name: c.path, value: c.count }));
      breadcrumbs.push({ label: drillPath, path: drillPath });
    } else {
      // No children — stay at top level
      chartData = data.map((d) => ({ name: d.path, value: d.count }));
      setDrillPath(null);
    }
  } else {
    chartData = data.map((d) => ({ name: d.path, value: d.count }));
  }

  const handleClick = (entry) => {
    if (!drillPath) {
      const match = data.find((d) => d.path === entry.name);
      if (match && match.children.length > 0) {
        setDrillPath(entry.name);
      }
    }
  };

  return (
    <Container>
      <Breadcrumb>
        {breadcrumbs.map((bc, i) => (
          <React.Fragment key={bc.label}>
            {i > 0 && <BreadcrumbSeparator>/</BreadcrumbSeparator>}
            {i < breadcrumbs.length - 1 || breadcrumbs.length === 1 ? (
              <BreadcrumbLink onClick={() => setDrillPath(bc.path)}>
                {bc.label}
              </BreadcrumbLink>
            ) : (
              <span>{bc.label}</span>
            )}
          </React.Fragment>
        ))}
      </Breadcrumb>

      <ResponsiveContainer width="100%" height={isMobile ? 280 : 400}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={isMobile ? 80 : 150}
            label={isMobile ? false : renderCustomLabel}
            onClick={handleClick}
            style={{ cursor: drillPath ? "default" : "pointer" }}
          >
            {chartData.map((_, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>

      {!drillPath && <HintText>Click a slice to drill into sub-paths</HintText>}
    </Container>
  );
};

export default LandingPagePieChart;
